/**
 * Eval Run Comparison Drawer — side-by-side diff of two evaluation runs.
 *
 * The highest-value piece: shows per-case pass/fail deltas where the same
 * caseId passed in one run and failed in the other (regression/improvement
 * detection), not just aggregate metric deltas.
 *
 * Scoped to exactly 2 runs for this phase.
 */
import { useEffect, useRef, type JSX } from 'react'
import {
  ArrowRight,
  CheckCircle2,
  GitCompare,
  Minus,
  TrendingDown,
  TrendingUp,
  X,
  XCircle,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Types (mirrors EvalRun from the page — kept local to avoid a shared import)
// ---------------------------------------------------------------------------

interface EvalResultCase {
  caseId: string
  prompt: string
  expected: string
  actual: string
  passed: boolean
  score: number
  latencyMs: number
  error?: string
}

interface EvalRun {
  id: string
  suiteId: string
  suiteName: string
  status: 'running' | 'completed' | 'failed'
  alias: string
  totalCases: number
  passedCases: number
  failedCases: number
  accuracyRate: number
  avgLatencyMs: number
  totalTokens: number
  createdAt: string
  completedAt?: string
  results: EvalResultCase[]
}

interface EvalRunCompareDrawerProps {
  runA: EvalRun | null
  runB: EvalRun | null
  isOpen: boolean
  onClose: () => void
}

// ---------------------------------------------------------------------------
// Focus trap
// ---------------------------------------------------------------------------

function useFocusTrap(
  ref: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  onEscape: () => void,
): void {
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onEscape()
      }
    }
    document.addEventListener('keydown', handler)
    return (): void => document.removeEventListener('keydown', handler)
  }, [isOpen, onEscape])
}

// ---------------------------------------------------------------------------
// Delta helpers
// ---------------------------------------------------------------------------

type CaseDelta = 'same-pass' | 'same-fail' | 'regression' | 'improvement'

function classifyCase(a: EvalResultCase | undefined, b: EvalResultCase | undefined): CaseDelta | null {
  if (!a || !b) return null
  if (a.passed && b.passed) return 'same-pass'
  if (!a.passed && !b.passed) return 'same-fail'
  if (a.passed && !b.passed) return 'regression'
  return 'improvement'
}

function MetricDelta({ a, b, unit, higherIsBetter }: { a: number; b: number; unit?: string; higherIsBetter: boolean }): JSX.Element {
  const delta = b - a
  const isBetter = higherIsBetter ? delta > 0 : delta < 0
  const isWorse = higherIsBetter ? delta < 0 : delta > 0
  const Icon = delta === 0 ? Minus : isBetter ? TrendingUp : TrendingDown
  const color = delta === 0 ? 'text-gray-400' : isBetter ? 'text-emerald-600' : isWorse ? 'text-red-600' : 'text-gray-400'
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`}>
      <Icon className="h-3 w-3" />
      {delta > 0 ? '+' : ''}{delta.toFixed(delta % 1 === 0 ? 0 : 2)}{unit ?? ''}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Drawer
// ---------------------------------------------------------------------------

export default function EvalRunCompareDrawer({
  runA,
  runB,
  isOpen,
  onClose,
}: EvalRunCompareDrawerProps): JSX.Element | null {
  const drawerRef = useRef<HTMLDivElement>(null)
  useFocusTrap(drawerRef, isOpen, onClose)

  if (!isOpen || !runA || !runB) return null

  const casesA = new Map(runA.results.map((c) => [c.caseId, c]))
  const casesB = new Map(runB.results.map((c) => [c.caseId, c]))
  const allCaseIds = Array.from(new Set([...casesA.keys(), ...casesB.keys()]))

  const regressions = allCaseIds.filter((id) => classifyCase(casesA.get(id), casesB.get(id)) === 'regression')
  const improvements = allCaseIds.filter((id) => classifyCase(casesA.get(id), casesB.get(id)) === 'improvement')

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="eval-compare-title">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        ref={drawerRef}
        className="absolute right-0 top-0 flex h-full w-full flex-col bg-white shadow-2xl md:w-[760px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 p-4">
          <div className="flex min-w-0 items-center gap-2">
            <GitCompare className="h-4 w-4 shrink-0 text-[#3454cf]" />
            <span id="eval-compare-title" className="min-w-0 truncate text-sm font-semibold text-gray-900">
              Compare Runs
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close drawer"
            className="rounded-sm p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-5 overflow-y-auto p-4 pb-6">
          {/* Run labels */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-sm border border-gray-200 bg-gray-50/50 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Run A</p>
              <p className="mt-0.5 truncate text-sm font-semibold text-gray-900">{runA.suiteName}</p>
              <p className="font-mono text-[10px] text-gray-500">{runA.alias} · {new Date(runA.createdAt).toLocaleDateString()}</p>
            </div>
            <div className="rounded-sm border border-gray-200 bg-gray-50/50 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Run B</p>
              <p className="mt-0.5 truncate text-sm font-semibold text-gray-900">{runB.suiteName}</p>
              <p className="font-mono text-[10px] text-gray-500">{runB.alias} · {new Date(runB.createdAt).toLocaleDateString()}</p>
            </div>
          </div>

          {/* Aggregate metric deltas */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-sm border border-gray-200 bg-white p-3 text-center shadow-sm">
              <span className="text-[10px] font-medium text-gray-500">Accuracy</span>
              <div className="mt-1 flex items-center justify-center gap-1.5">
                <span className="text-lg font-bold text-gray-900">{runA.accuracyRate}%</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="text-lg font-bold text-gray-900">{runB.accuracyRate}%</span>
              </div>
              <div className="mt-1"><MetricDelta a={runA.accuracyRate} b={runB.accuracyRate} unit="%" higherIsBetter /></div>
            </div>
            <div className="rounded-sm border border-gray-200 bg-white p-3 text-center shadow-sm">
              <span className="text-[10px] font-medium text-gray-500">Avg Latency</span>
              <div className="mt-1 flex items-center justify-center gap-1.5">
                <span className="text-lg font-bold text-gray-900">{runA.avgLatencyMs}ms</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="text-lg font-bold text-gray-900">{runB.avgLatencyMs}ms</span>
              </div>
              <div className="mt-1"><MetricDelta a={runA.avgLatencyMs} b={runB.avgLatencyMs} unit="ms" higherIsBetter={false} /></div>
            </div>
            <div className="rounded-sm border border-gray-200 bg-white p-3 text-center shadow-sm">
              <span className="text-[10px] font-medium text-gray-500">Tokens</span>
              <div className="mt-1 flex items-center justify-center gap-1.5">
                <span className="text-lg font-bold text-gray-900">{runA.totalTokens.toLocaleString()}</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="text-lg font-bold text-gray-900">{runB.totalTokens.toLocaleString()}</span>
              </div>
              <div className="mt-1"><MetricDelta a={runA.totalTokens} b={runB.totalTokens} higherIsBetter={false} /></div>
            </div>
            <div className="rounded-sm border border-gray-200 bg-white p-3 text-center shadow-sm">
              <span className="text-[10px] font-medium text-gray-500">Passed</span>
              <div className="mt-1 flex items-center justify-center gap-1.5">
                <span className="text-lg font-bold text-gray-900">{runA.passedCases}/{runA.totalCases}</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="text-lg font-bold text-gray-900">{runB.passedCases}/{runB.totalCases}</span>
              </div>
              <div className="mt-1"><MetricDelta a={runA.passedCases} b={runB.passedCases} higherIsBetter /></div>
            </div>
          </div>

          {/* Regression / improvement summary */}
          {(regressions.length > 0 || improvements.length > 0) && (
            <div className="flex flex-wrap gap-2">
              {regressions.length > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-sm bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700">
                  <TrendingDown className="h-3.5 w-3.5" />
                  {regressions.length} regression{regressions.length !== 1 ? 's' : ''}
                </span>
              )}
              {improvements.length > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-sm bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                  <TrendingUp className="h-3.5 w-3.5" />
                  {improvements.length} improvement{improvements.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}

          {/* Per-case deltas */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Case-by-case ({allCaseIds.length})
            </h3>
            {allCaseIds.length === 0 ? (
              <p className="text-sm text-gray-500">No cases to compare.</p>
            ) : (
              <div className="space-y-1.5">
                {allCaseIds.map((caseId) => {
                  const cA = casesA.get(caseId)
                  const cB = casesB.get(caseId)
                  const delta = classifyCase(cA, cB)
                  const isRegression = delta === 'regression'
                  const isImprovement = delta === 'improvement'
                  const rowClass = isRegression
                    ? 'border-red-200 bg-red-50/30'
                    : isImprovement
                      ? 'border-emerald-200 bg-emerald-50/30'
                      : 'border-gray-200 bg-white'

                  return (
                    <div key={caseId} className={`rounded-sm border p-2.5 ${rowClass}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          {/* Run A status */}
                          {cA?.passed ? (
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                          ) : cA ? (
                            <XCircle className="h-3.5 w-3.5 text-red-500" />
                          ) : (
                            <Minus className="h-3.5 w-3.5 text-gray-300" />
                          )}
                          <span className="text-xs font-semibold text-gray-900">{caseId}</span>
                          {/* Run B status */}
                          <ArrowRight className="h-3 w-3 text-gray-300" />
                          {cB?.passed ? (
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                          ) : cB ? (
                            <XCircle className="h-3.5 w-3.5 text-red-500" />
                          ) : (
                            <Minus className="h-3.5 w-3.5 text-gray-300" />
                          )}
                        </div>
                        {isRegression && (
                          <span className="rounded-sm bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">Regression</span>
                        )}
                        {isImprovement && (
                          <span className="rounded-sm bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">Improved</span>
                        )}
                      </div>
                      {(cA?.error || cB?.error) && (
                        <p className="mt-1 truncate text-[10px] text-red-600">
                          {cB?.error ?? cA?.error}
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
